import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface Meeting {
  id: number;
  title: string;
  password_protection: boolean;
  meeting_password?: string;
  waiting_room: boolean;
  mute_on_entry: boolean;
  allow_recording: boolean;
  meeting_date?: Date;
  start_time?: string;
  end_time?: string;
  meeting_type: 'instant' | 'scheduled';
  description?: string;
  participants: string[];
  participantDetails?: {
    id: number;
    email: string;
    accepted: boolean;
    user?: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
    };
  }[];
  meeting_link: string;
  created_by: number;
  completed_status: boolean;
  canceled_meeting: boolean;
  is_recurring?: boolean;
  is_recurring_instance?: boolean;
  repeat_status?: 'daily' | 'weekly' | 'bi-weekly' | 'monthly' | 'yearly';
  repeat_days?: string[];
  created_at: Date;
  createdById: number;
}

interface MeetingData {
  meeting: Meeting;
}

interface CreateMeetingData {
  title: string;
  passwordProtection?: boolean;
  meetingPassword?: string;
  waitingRoom?: boolean;
  muteOnEntry?: boolean;
  allowRecording?: boolean;
  meetingDate?: Date;
  startTime?: string;
  endTime?: string;
  meetingType?: 'instant' | 'scheduled';
  description?: string;
  participants?: string[];
  participantDetails?: { email: string; name?: string; is_moderator?: boolean }[];
  isRecurring?: boolean;
  repeatStatus?: 'daily' | 'weekly' | 'bi-weekly' | 'monthly' | 'yearly';
  repeatDays?: string[];
  recurringStartDate?: Date;
  recurringEndDate?: Date;
  meeting_link?: string;
  meetingId?: string;
  organizerTimezone?: string;
}

interface MeetingContextType {
  meetings: Meeting[];
  loading: boolean;
  error: string | null;
  createMeeting: (meetingData: CreateMeetingData) => Promise<Meeting>;
  getUserMeetings: () => Promise<void>;
  cancelMeeting: (meetingId: number) => Promise<void>;
  editMeeting: (meetingId: number, updates: Partial<CreateMeetingData>) => Promise<void>;
  clearError: () => void;
  acceptInvite: (token: string, inviteJwtToken?: string) => Promise<void>;
  getParticipantMeetings: () => Promise<void>;
  sendInvites: (meetingId: string, emails: string[]) => Promise<void>;
  joinMeeting: (meetingId: string | number, meetingPassword?: string) => Promise<any>;
  getMeetingDetails: (meetingId: number) => Promise<any>;
  syncMeetingToCalendars: (meetingId: number) => Promise<any>;
  fetchAllMeetings: () => Promise<void>;
  jitsiToken: (meetingId: number, isModerator: boolean) => Promise<string>;
  jitsiTokenFromInvite: (inviteJwtToken: string) => Promise<string>;
  getMeetingById: (meetingId: number) => Promise<MeetingData>;
  setUpdateNotification: (value: boolean) => void;
  updateNotification: boolean;
  notificationMessage: string | null; // <-- New field
  showNotification: (message: string) => void; // <-- New function
  clearNotification: () => void; // <-- New function
  // generateInviteJwtToken: (inviteToken: string) => Promise<string>;
}

const MeetingContext = createContext<MeetingContextType | undefined>(undefined);

export const useMeeting = () => {
  const context = useContext(MeetingContext);
  if (context === undefined) {
    throw new Error('useMeeting must be used within a MeetingProvider');
  }
  return context;
};

interface MeetingProviderProps {
  children: ReactNode;
}

export const MeetingProvider: React.FC<MeetingProviderProps> = ({ children }) => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  // console.log("Initial meetings state:", meetings);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://vela-back.wingtrill.com';
  const { token, logout } = useAuth();
  // console.log("Auth token:", token);

  const [updateNotification, setUpdateNotification] = useState<boolean>(false);

  const [notificationMessage, setNotificationMessage] = useState<string | null>(null); // <-- New state

  const showNotification = (message: string) => {
    setNotificationMessage(message);
    // Optional: Auto-hide after 5 seconds
    setTimeout(() => {
      setNotificationMessage(null);
    }, 5000);
  };

  const clearNotification = () => setNotificationMessage(null);
  const clearError = () => setError(null);

  const createMeeting = async (meetingData: CreateMeetingData) => {
    setLoading(true);
    clearError();

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await fetch(`${backendUrl}/api/meetings/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(meetingData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create meeting');
      }

      setMeetings(prev => [data.meeting, ...prev]);
      if (meetingData.meetingType === 'scheduled') {
        await syncMeetingToCalendars(data.meeting.id);
      }
      console.log("Created meetingdjfnj:", data.meeting);

      return data.meeting;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getUserMeetings = async () => {
    setLoading(true);
    clearError();

    try {
      // const token = localStorage.getItem('token');
      if (!token) {
        logout();
      }

      const response = await fetch(`${backendUrl}/api/meetings/my-meetings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle token-related errors
        if (data.message === 'Invalid token' || data.message === 'jwt expired' || data.message === 'Authentication required') {
          console.warn('Token invalid or expired — clearing from localStorage');
          localStorage.removeItem('token');

          // Optional: redirect to login page if using React Router
          window.location.href = '/login';
        }

        throw new Error(data.message || 'Failed to fetch meetings');
      }

      // Update meetings state with both created and participant meetings
      setMeetings(data.meetings);
      console.log('User meetings:', data.meetings);

    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching meetings:', err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const cancelMeeting = async (meetingId: number) => {
    setLoading(true);
    clearError();

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await fetch(`${backendUrl}/api/meetings/cancel/${meetingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to cancel meeting');
      }

      setMeetings(prev =>
        prev.map(meeting =>
          meeting.id === meetingId ? { ...meeting, canceled_meeting: true } : meeting
        )
      );
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const editMeeting = async (meetingId: number, updates: Partial<CreateMeetingData>) => {
    setLoading(true);
    clearError();

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await fetch(`${backendUrl}/api/meetings/edit/${meetingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update meeting');
      }

      setMeetings(prev =>
        prev.map(meeting =>
          meeting.id === meetingId ? { ...meeting, ...data.meeting } : meeting
        )
      );
      fetchAllMeetings();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const acceptInvite = async (token: string, inviteJwtToken?: string) => {
    setLoading(true);
    clearError();

    try {
      const authToken = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/meetings/accept-invite/${token}`, {
        method: 'POST',
        headers: {
          // 'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ inviteJwtToken })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to accept invite');
      }

      // Refresh meetings list after accepting invite
      await getUserMeetings();
      await fetchAllMeetings();

      return data;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  const getParticipantMeetings = async () => {
    setLoading(true);
    clearError();

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await fetch(`${backendUrl}/api/meetings/participant-meetings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch participant meetings');
      }

      setMeetings(prev => [...prev, ...data.meetings]);
      console.log('Participant meetings:', data.meetings);

    }
    catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const sendInvites = async (meetingId: string, emails: string[]) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/meetings/invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ meetingId, emails }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);

      // alert("Invitations sent successfully!");
      showNotification("Invitations sent successfully!");
      return data;
    } catch (error) {
      console.error("Error sending invites:", error);

      // alert(error.message);
    }
  };


  const joinMeeting = async (meetingId: string | number, meetingPassword?: string) => {
    setLoading(true);
    clearError();

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await fetch(`${backendUrl}/api/meetings/${meetingId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ meetingPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to join meeting');
      }

      return data; // Returns { message, token, meeting, userRole }
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getMeetingDetails = async (meetingId: number) => {
    setLoading(true);
    clearError();

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await fetch(`${backendUrl}/api/meetings/${meetingId}/details`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch meeting details');
      }

      return data; // Returns { meeting, userRole, participantsCount }
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };



  const syncMeetingToCalendars = async (meetingId: number) => {
    setLoading(true);
    clearError();

    try {
      const response = await fetch(`${backendUrl}/api/meetings/${meetingId}/sync-calendars`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to sync meeting with calendars');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error syncing meeting to calendars:', error);
      // setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };




  const fetchAllMeetings = async () => {
    setLoading(true);
    clearError();

    try {
      const [userRes, participantRes] = await Promise.all([
        fetch(`${backendUrl}/api/meetings/my-meetings`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${backendUrl}/api/meetings/participant-meetings`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      const [userData, participantData] = await Promise.all([
        userRes.json(),
        participantRes.json(),
      ]);

      if (!userRes.ok || !participantRes.ok) {
        const msg = userData.message || participantData.message || 'Failed to fetch meetings';
        throw new Error(msg);
      }


      const combined = [...userData.meetings, ...participantData.meetings];
      const uniqueMeetings = Array.from(
        new Map(combined.map(m => [m.id, m])).values()
      );


      const sortedMeetings = uniqueMeetings.sort((a, b) => {

        const createdAtA = new Date(a.created_at).getTime();
        const createdAtB = new Date(b.created_at).getTime();

        if (createdAtB !== createdAtA) {
          return createdAtB - createdAtA;
        }

        const dateA = new Date(a.meeting_date || 0).getTime();
        const dateB = new Date(b.meeting_date || 0).getTime();

        if (dateB !== dateA) {
          return dateB - dateA;
        }
        return b.id - a.id;
      });

      setMeetings(sortedMeetings);

    } catch (err: any) {
      console.error('Error fetching meetings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const jitsiToken = async (meetingId: number, isModerator: boolean) => {
    setLoading(true);
    clearError();

    try {
      const response = await fetch(`${backendUrl}/api/meetings/generate-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ meetingId, isModerator }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate Jitsi token');
      }

      const data = await response.json();
      return data.token;
    } catch (error) {
      console.error('Error generating Jitsi token:', error);
      // setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const jitsiTokenFromInvite = async (inviteJwtToken: string) => {
    setLoading(true);
    clearError();

    try {
      const response = await fetch(`${backendUrl}/api/meetings/generate-token-from-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ inviteJwtToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate Jitsi token from invite');
      }

      const data = await response.json();
      return data.token;
    } catch (error) {
      console.error('Error generating Jitsi token from invite:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getMeetingById = async (meetingId: number) => {
    setLoading(true);
    clearError();

    try {
      const response = await fetch(`${backendUrl}/api/meetings/${meetingId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch meeting details');
      }

      const data = await response.json();
      return data; // Returns { meeting, userRole, participantsCount }
    } catch (error) {
      console.error('Error fetching meeting details:', error);
      // setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };



  return (
    <MeetingContext.Provider
      value={{
        meetings,
        loading,
        error,
        createMeeting,
        getUserMeetings,
        cancelMeeting,
        editMeeting,
        clearError,
        acceptInvite,
        getParticipantMeetings,
        sendInvites,
        joinMeeting,
        getMeetingDetails,
        syncMeetingToCalendars,
        fetchAllMeetings,
        jitsiToken,
        jitsiTokenFromInvite,
        getMeetingById,
        notificationMessage,
        showNotification,
        clearNotification,
        setUpdateNotification,
        updateNotification,
        // generateInviteJwtToken,
      }}
    >
      {children}
    </MeetingContext.Provider>
  );
};